import { Link } from 'react-router-dom';
import LegalPage, { LegalSection, company } from './LegalPage';
import { useLocale } from '../../context/LocaleContext';
import { termsAr } from '../../i18n/legalAr';

const Terms = () => {
  const { lang } = useLocale();
  if (lang === 'ar') {
    return (
      <LegalPage title={termsAr.title} intro={termsAr.intro}>
        {termsAr.sections.map((section) => (
          <LegalSection key={section.id} id={section.id} title={section.title}>
            {section.paragraphs.map((paragraph) => (
              <p key={paragraph}>{paragraph}</p>
            ))}
            {section.id === 'law' && (
              <p>
                <Link to="/privacy" className="text-purple-700 hover:underline">سياسة الخصوصية</Link>
              </p>
            )}
          </LegalSection>
        ))}
      </LegalPage>
    );
  }

  return (
  <LegalPage
    title="Terms and conditions for customers"
    intro={`These are the customer terms of ${company.product}. ${company.product} is operated by ${company.name}, the company, at ${company.address}. Buying a ticket, creating an account, or using ${company.product} means you accept these terms.`}
  >
    <LegalSection id="who" title="1. Who we are">
      <p>
        {company.product} is the ticketing service. {company.name} is the company that operates it, at {company.address}. The company website is{' '}
        <a href={company.site} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{company.siteLabel}</a>.
        In these terms, “we” and “us” mean {company.product}. “You” means the person using {company.product} to browse events or buy tickets.
      </p>
    </LegalSection>

    <LegalSection id="definitions" title="2. Definitions">
      <p><span className="text-gray-900">Event</span> means a performance, match, conference, or other gathering listed on Ticketaty.</p>
      <p><span className="text-gray-900">Organizer</span> means the person or business responsible for putting on the Event. The Organizer sets the event rules, the venue conditions, and whether an event is changed or cancelled.</p>
      <p><span className="text-gray-900">Ticket</span> means a paid or complimentary right of entry, issued as a QR code, barcode, reference, or other pass through Ticketaty.</p>
      <p><span className="text-gray-900">Website</span> means the Ticketaty pages, including those opened on a phone, tablet, or computer.</p>
    </LegalSection>

    <LegalSection id="account" title="3. Your account">
      <p>
        You must give a real name, a working email, and, if you add one, a mobile number you control. You are responsible for keeping your password private and for orders placed from your account.
      </p>
      <p>
        We may refuse, suspend, or close an account that is used for fraud, resale that breaks these terms, or abuse of the website.
      </p>
    </LegalSection>

    <LegalSection id="purchase" title="4. Buying a ticket">
      <p>
        A ticket is offered by the Organizer. Ticketaty provides the listing, checkout, and delivery of the ticket. When you pay, you are buying entry to that Organizer’s event on the terms shown on the event page, including date, venue, category, and any age or entry rules.
      </p>
      <p>
        Check the event name, date, venue, and quantity before you pay. An order is confirmed when payment succeeds and the ticket is issued to your account or email.
      </p>
    </LegalSection>

    <LegalSection id="price" title="5. Price and payment">
      <p>
        The price shown at checkout is the price you pay, including any fee that is displayed before you confirm. Taxes, if they apply, are included in the amount shown or stated next to it.
      </p>
      <p>
        Payment is collected through the payment method offered at checkout. If a payment is declined, reversed, or made with a card you are not allowed to use, we may cancel the tickets without a replacement.
      </p>
    </LegalSection>

    <LegalSection id="delivery" title="6. Ticket delivery">
      <p>
        Tickets are usually delivered in your Ticketaty account and by email after payment. You must keep the ticket as carefully as a printed pass. If someone else scans it first, the venue can refuse a second entry, and we do not replace a ticket that was shared, copied, or lost.
      </p>
    </LegalSection>

    <LegalSection id="entry" title="7. Entry to the event">
      <p>
        The Organizer and the venue control admission. They may ask for the ticket, a matching photo ID, and the card used to pay. Arrive early enough to clear entry checks. Late arrival, a dress code, bag rules, and age limits are set by the Organizer, not by Ticketaty.
      </p>
      <p>
        A ticket is valid for the named event, date, and category. It is not a promise of a particular view unless the listing says so.
      </p>
    </LegalSection>

    <LegalSection id="changes" title="8. Changes, postponement, and cancellation">
      <p>
        The Organizer may change the time, venue, lineup, or other details, or may postpone or cancel the event. We will pass on the update we receive, using the email or phone on your order where we can.
      </p>
      <p>
        If an event is cancelled and not rescheduled, the Organizer’s refund rule on the event page applies. Where that page is silent, the ticket price is refunded to the original payment method. A change of support act, seating within the same category, or a reasonable change of start time is not by itself a cancellation.
      </p>
    </LegalSection>

    <LegalSection id="refunds" title="9. Refunds">
      <p>
        Tickets are generally not refundable and not exchangeable once issued, except where the event is cancelled, where the law in Qatar requires a refund, or where the event page states a cooling-off or refund rule.
      </p>
      <p>
        We do not refund a ticket because you changed your plans, bought the wrong date, or no longer want to attend. Service fees are refunded only when the event is cancelled and the fee was charged by Ticketaty for a ticket that is refunded in full.
      </p>
    </LegalSection>

    <LegalSection id="resale" title="10. Resale">
      <p>
        Do not resell a Ticketaty ticket on another website or above the price you paid, unless the Organizer has allowed that on the event page. We may cancel a ticket we believe was resold in breach of these terms. Entry can be refused, and no refund is due in that case.
      </p>
      <p>
        You may give a ticket to a guest who will enter with you, if the Organizer allows it. You remain responsible for that guest’s use of the ticket.
      </p>
    </LegalSection>

    <LegalSection id="use" title="11. Using the website">
      <p>
        Do not misuse Ticketaty. That includes attempting to break checkout, scrape the site in a way that degrades it, create fake accounts, or interfere with another customer’s order.
      </p>
      <p>
        Event descriptions and images are provided so you can decide whether to attend. The Organizer is responsible for the accuracy of the event content they supply.
      </p>
    </LegalSection>

    <LegalSection id="liability" title="12. Liability">
      <p>
        {company.product} is a ticketing service. The Organizer is responsible for the event itself, for safety at the venue, and for the experience you receive there. {company.product} is not the promoter of an event unless a listing says that {company.name} is the organizer.
      </p>
      <p>
        Nothing in these terms limits liability that cannot be limited under the laws of the State of Qatar. Otherwise, our liability for a claim about a ticket is limited to the amount you paid for that ticket through Ticketaty.
      </p>
    </LegalSection>

    <LegalSection id="ip" title="13. Intellectual property">
      <p>
        The {company.product} name, layout, and software belong to {company.name}, the company that operates {company.product}, or its licensors. Event names, artwork, and performer names belong to their owners. You may not copy the website or the tickets except as needed to attend the event.
      </p>
    </LegalSection>

    <LegalSection id="law" title="14. Law and contact">
      <p>
        These terms are governed by the laws of the State of Qatar. Disputes are subject to the courts of Qatar, without limiting any mandatory right you have as a consumer.
      </p>
      <p>
        Questions about these {company.product} terms: {company.name}, {company.address}. Phone and WhatsApp {company.phone}. Website{' '}
        <a href={company.site} target="_blank" rel="noopener noreferrer" className="text-purple-700 hover:underline">{company.siteLabel}</a>.
      </p>
      <p>
        How we handle personal data is described in the <Link to="/privacy" className="text-purple-700 hover:underline">Privacy policy</Link>.
      </p>
    </LegalSection>
  </LegalPage>
  );
};

export default Terms;
